import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { TEXT_COLOR } from '../../theme';
import {
    getPossibleCurvesDetail,
    getCurveTopLevel,
    curvePickForReply,
    CURVE_NAMES,
} from '../../Jacques';
import {
    getAllCurvePaths, getCandidate, domainLevelToSublevel,
} from '../../utils/jacquesGeometry';
import JacquesChartBase from '../JacquesChartBase';

// Vista de apoyo al etiquetado (solo staff): en vez del único resultado que
// termina en el reporte, dibuja TODAS las curvas de Jacques que pasan por el
// casillero (edad al momento del test + nivel etiquetado), resaltando la que la
// estrategia vigente elige. Sirve para ver el abanico de potenciales que abre
// cada etiqueta antes de guardarla.

const WIDTH = 820;
const HEIGHT = 560;
const MAX_WIDTH = 980;

// Las curvas que pasan por el casillero van todas en el mismo azul: lo que las
// separa del fondo es el color (azul contra gris), y entre ellas se distinguen
// por el nombre rotulado sobre cada trazo. La elegida va opaca y más gruesa; las
// otras, algo más livianas.
const POSSIBLE_BLUE = '#3B5BD9';
const PICKED_OPACITY = 1;
const POSSIBLE_OPACITY = 0.7;

// Trama de fondo: el resto de las curvas del modelo, las que no pasan por este
// casillero. Van en gris translúcido para que se lean como contexto y no
// compitan con las curvas posibles ni con las verdes de referencia.
const ALL_CURVES_GREY = '#98A1AB';
const ALL_CURVES_OPACITY = 0.5;
// Los rótulos de esas curvas van en un gris más firme: el del trazo, atenuado,
// no daría contraste suficiente para leerse.
const ALL_CURVES_LABEL = '#5A6472';

const NUMBER_TO_ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];

// '4A' -> 'IV A'
function toRoman(nivel) {
    if (!nivel) return '';
    const roman = NUMBER_TO_ROMAN[Number(nivel.charAt(0))];
    return roman ? `${roman} ${nivel.charAt(1)}` : nivel;
}

// 'VIIA' -> 'VII A'. El nivel al que llega una curva gris se toma de su
// geometría (jacquesGeometry) y no de la tabla `curvas` de Jacques.js: esa
// tabla llega hasta 6A, así que las curvas más altas del abanico (6, 6.1, 7 y
// 7.1) quedarían todas rotuladas 'VI A' pese a dibujarse a alturas distintas.
function romanFromSublevel(sublevel) {
    if (!sublevel) return '';
    return `${sublevel.slice(0, -1)} ${sublevel.slice(-1)}`;
}

// Nivel de llegada de una curva del abanico gris, ya rotulado.
function curveEndLabel(curve) {
    return curve.endLevel === undefined
        // La curva 0 es un trazo fijo en píxeles, sin nivel de dominio: se
        // rotula con la tabla, que para ella sí tiene el dato.
        ? toRoman(getCurveTopLevel(curve.curva))
        : romanFromSublevel(domainLevelToSublevel(curve.endLevel));
}

export default function PossibleCurvesCard({
    tr, language, companyChart, level, sublevel, age, createdDate,
}) {
    const detail = useMemo(() => {
        if (!level || !sublevel || age == null) return null;
        return getPossibleCurvesDetail(
            age, `${level}${sublevel}`, tr, curvePickForReply(createdDate),
        );
    }, [level, sublevel, age, createdDate, tr]);

    const curves = detail?.curves ?? [];

    return (
        <Box sx={{
            bgcolor: '#fff',
            border: '1px solid #E1E4E8',
            borderRadius: '14px',
            boxShadow: '0 1px 2px rgba(15,17,35,0.04)',
            p: 3,
        }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: TEXT_COLOR }}>
                {tr('possible_curves')}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: TEXT_COLOR, opacity: 0.7, mt: 0.5 }}>
                {tr('possible_curves_help')}
            </Typography>

            {!detail && (
                <Typography sx={{ fontSize: '14px', color: TEXT_COLOR, opacity: 0.7, mt: 2.5 }}>
                    {tr('possible_curves_empty')}
                </Typography>
            )}

            {detail && curves.length === 0 && (
                <Typography sx={{ fontSize: '14px', color: TEXT_COLOR, opacity: 0.7, mt: 2.5 }}>
                    {tr('possible_curves_none')}
                </Typography>
            )}

            {curves.length > 0 && (
                <>
                    <CurvesLegend tr={tr} curves={curves} picked={detail.picked} />

                    <Box sx={{ mt: 2 }}>
                        <JacquesChartBase
                            width={WIDTH}
                            height={HEIGHT}
                            maxWidth={MAX_WIDTH}
                            companyChart={companyChart}
                            language={language}
                            bare
                            showReferenceCurves={false}
                        >
                            {({ project, innerW, innerH, showTip, hideTip }) => (
                                <CurvesOverlay
                                    tr={tr}
                                    curves={curves}
                                    picked={detail.picked}
                                    age={age}
                                    project={project}
                                    innerW={innerW}
                                    innerH={innerH}
                                    showTip={showTip}
                                    hideTip={hideTip}
                                />
                            )}
                        </JacquesChartBase>
                    </Box>
                </>
            )}
        </Box>
    );
}

// Leyenda: un ítem por curva, con su color, su nombre (0.1, 1.2, 3.3, ...), el
// nivel al que llega y la marca de cuál queda seleccionada.
function CurvesLegend({ tr, curves, picked }) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {curves.map((c) => {
                const isPicked = c.curva === picked;
                return (
                    <Box
                        key={c.curva}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '999px',
                            border: `1px solid ${isPicked ? POSSIBLE_BLUE : '#E1E4E8'}`,
                            bgcolor: isPicked ? '#F7F9FE' : 'transparent',
                        }}
                    >
                        <Box sx={{
                            width: 14,
                            height: isPicked ? 3 : 2.25,
                            borderRadius: '2px',
                            bgcolor: POSSIBLE_BLUE,
                            opacity: isPicked ? PICKED_OPACITY : POSSIBLE_OPACITY,
                        }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: TEXT_COLOR }}>
                            {tr('curve')} {c.nombre}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: TEXT_COLOR, opacity: 0.75 }}>
                            · {tr('reaches')} {toRoman(c.nivelPot)}
                        </Typography>
                        {isPicked && (
                            <Typography sx={{
                                fontSize: '11px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                color: POSSIBLE_BLUE,
                            }}>
                                {tr('selected_curve')}
                            </Typography>
                        )}
                    </Box>
                );
            })}

            {/* La trama de fondo también entra en la leyenda: sin esto el gris
                translúcido no tiene nombre. */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: '999px',
                border: '1px solid #E1E4E8',
            }}>
                <Box sx={{
                    width: 14,
                    height: 2.25,
                    borderRadius: '2px',
                    bgcolor: ALL_CURVES_GREY,
                    opacity: ALL_CURVES_OPACITY,
                }} />
                <Typography sx={{ fontSize: '13px', color: TEXT_COLOR, opacity: 0.75 }}>
                    {tr('other_curves')}
                </Typography>
            </Box>
        </Box>
    );
}

function CurvesOverlay({ tr, curves, picked, age, project, innerW, innerH, showTip, hideTip }) {
    const traces = useMemo(() => curves.map((c) => ({
        ...c,
        isPicked: c.curva === picked,
        opacity: c.curva === picked ? PICKED_OPACITY : POSSIBLE_OPACITY,
        ...getCandidate(c.curva, age, project),
    })), [curves, picked, age, project]);

    // Las curvas nacen casi en el mismo punto (el casillero), así que sus
    // etiquetas se pisarían: las separo verticalmente empujando hacia arriba,
    // de abajo hacia arriba, con una distancia mínima.
    const labelY = useMemo(() => {
        const MIN_GAP = 15;
        const placed = new Map();
        let prev = null;
        traces
            .filter((c) => c.dot)
            .sort((a, b) => b.dot.y - a.dot.y)
            .forEach((c) => {
                const y = prev === null ? c.dot.y + 4 : Math.min(c.dot.y + 4, prev - MIN_GAP);
                placed.set(c.curva, y);
                prev = y;
            });
        return placed;
    }, [traces]);

    // Todas las curvas del modelo, para ver el abanico completo detrás de las
    // que corresponden a este casillero. Van todas con el mismo trazo gris (la
    // capa verde de referencia está apagada en esta vista), así lo único que
    // resalta es qué curvas abre la etiqueta elegida.
    const allCurves = useMemo(
        () => getAllCurvePaths(project).filter((c) => c.path),
        [project],
    );
    const possibleSet = useMemo(() => new Set(curves.map((c) => c.curva)), [curves]);

    // Nivel al que llega cada curva, rotulado sobre su extremo derecho. Los
    // extremos caen a alturas parecidas, así que se resuelven los solapes entre
    // TODOS los rótulos juntos (grises y azules) empujando hacia abajo, y se
    // acotan al alto del plot para que el clip no se coma ninguno.
    const endLabels = useMemo(() => {
        const items = [
            ...allCurves
                .filter((c) => !possibleSet.has(c.curva) && c.end)
                .map((c) => ({
                    key: `end-all-${c.curva}`,
                    x: c.end.x,
                    y: c.end.y,
                    text: curveEndLabel(c),
                    color: ALL_CURVES_LABEL,
                    opacity: 1,
                    weight: 500,
                })),
            ...traces
                .filter((c) => c.points.length)
                .map((c) => ({
                    key: `end-pos-${c.curva}`,
                    x: c.points[c.points.length - 1].x,
                    y: c.points[c.points.length - 1].y,
                    text: toRoman(c.nivelPot),
                    color: POSSIBLE_BLUE,
                    opacity: c.opacity,
                    weight: 700,
                })),
        ].filter((it) => it.text);

        const MIN_GAP = 12;
        const top = 10;
        const bottom = innerH - 4;
        items.sort((a, b) => a.y - b.y);

        const placed = [];
        for (let i = 0; i < items.length; i++) {
            const wanted = i === 0 ? items[i].y : Math.max(items[i].y, placed[i - 1].y + MIN_GAP);
            placed.push({
                ...items[i],
                x: Math.min(items[i].x, innerW) - 3,
                y: Math.min(bottom, Math.max(top, wanted)),
            });
        }
        return placed;
    }, [allCurves, possibleSet, traces, innerW, innerH]);

    const tip = (c) => `${tr('curve')} ${c.nombre} · ${tr('reaches')} ${toRoman(c.nivelPot)}`;
    const greyTip = (curve) =>
        `${tr('curve')} ${CURVE_NAMES[curve.curva]} · ${tr('reaches')} ${curveEndLabel(curve)}`;

    return (
        <>
            {allCurves.map((c) => (possibleSet.has(c.curva) ? null : (
                <g key={`all-${c.curva}`}>
                    <path
                        d={c.path}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={8}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => showTip(e, greyTip(c))}
                        onMouseMove={(e) => showTip(e, greyTip(c))}
                        onMouseLeave={hideTip}
                    />
                    <path
                        d={c.path}
                        fill="none"
                        stroke={ALL_CURVES_GREY}
                        strokeWidth={2.25}
                        strokeOpacity={ALL_CURVES_OPACITY}
                        pointerEvents="none"
                    />
                </g>
            )))}

            {traces.map((c) => {
                if (!c.path) return null;
                return (
                    <g key={c.curva}>
                        {/* Zona de hover más ancha que el trazo visible */}
                        <path
                            d={c.path}
                            fill="none"
                            stroke="transparent"
                            strokeWidth={16}
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={(e) => showTip(e, tip(c))}
                            onMouseMove={(e) => showTip(e, tip(c))}
                            onMouseLeave={hideTip}
                        />
                        <path
                            d={c.path}
                            fill="none"
                            stroke={POSSIBLE_BLUE}
                            strokeOpacity={c.opacity}
                            strokeWidth={c.isPicked ? 3.5 : 2.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeDasharray={c.isPicked ? '8 6' : '4 5'}
                            pointerEvents="none"
                        />
                    </g>
                );
            })}

            {/* Punto de arranque de cada curva, en la edad del test, con el nombre
                de la curva como etiqueta directa: ahí los trazos están separados
                verticalmente (cada curva cruza el casillero a distinta altura),
                mientras que al final se juntan contra el borde derecho. */}
            {traces.map((c) => {
                if (!c.dot) return null;
                // A la izquierda del punto salvo cerca del borde, donde el texto
                // se saldría del área dibujable.
                const leftSide = c.dot.x > 60;
                return (
                    <g key={`dot-${c.curva}`}>
                        <circle
                            cx={c.dot.x}
                            cy={c.dot.y}
                            r={c.isPicked ? 7 : 5}
                            fill={POSSIBLE_BLUE}
                            fillOpacity={c.opacity}
                            stroke="#fff"
                            strokeWidth={2}
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={(e) => showTip(e, tip(c))}
                            onMouseMove={(e) => showTip(e, tip(c))}
                            onMouseLeave={hideTip}
                        />
                        <text
                            x={c.dot.x + (leftSide ? -11 : 11)}
                            y={labelY.get(c.curva) ?? c.dot.y + 4}
                            fontSize={12}
                            fontWeight={700}
                            fill={POSSIBLE_BLUE}
                            textAnchor={leftSide ? 'end' : 'start'}
                            stroke="#fff"
                            strokeWidth={3}
                            paintOrder="stroke"
                            pointerEvents="none"
                        >
                            {c.nombre}
                        </text>
                    </g>
                );
            })}

            {/* Nivel al que llega cada curva, sobre su extremo derecho */}
            {endLabels.map((it) => (
                <text
                    key={it.key}
                    x={it.x}
                    y={it.y}
                    fontSize={11}
                    fontWeight={it.weight}
                    fill={it.color}
                    fillOpacity={it.opacity}
                    textAnchor="end"
                    stroke="#fff"
                    strokeWidth={3}
                    paintOrder="stroke"
                    pointerEvents="none"
                >
                    {it.text}
                </text>
            ))}
        </>
    );
}
